import type { AnnouncerAudioEngine } from '@src/audio/AnnouncerAudioEngine';
import type { Sfx } from '@src/audio/Sfx';
import { SfxId } from '@src/audio/Sfx';
import { DIFFICULTIES, difficultyIndex, saveDifficulty, setDifficulty } from '@src/core/Difficulty';
import { nudgeMobileChrome, requestGameFullscreen, viewportSize } from '@src/boot/fullscreen';
import {
  CONTESTANT_NAME_MAX,
  contestantName,
  fetchTopScores,
  filterContestantInput,
  setContestantName,
  type LeaderboardEntry,
} from '@src/leaderboard';

export interface StartScreenAudio {
  announcer: AnnouncerAudioEngine;
  sfx: Sfx;
  /** First user gesture: resume the AudioContext and start the menu theme. */
  playMenuMusic: () => Promise<void>;
  /** Confirm: stop streamed + procedural menu and cut leftover Vaelen / menu tails. */
  leaveMenu: () => void;
  /** True once the context is actually running (gamepad presses cannot unlock it). */
  audioRunning: () => boolean;
}

export interface StartScreenOptions {
  host: HTMLElement;
  audio: StartScreenAudio;
  /** Boot progress line under the menu. */
  status: HTMLElement;
}

export interface StartChoice {
  /** Player asked for the full Arcane Broadcast cut regardless of the seen flag. */
  replayIntro: boolean;
}

const ART_SRC = '/assets/ui/title_keyart.jpg';
const RUNES = ['\u16B1', '\u16A0', '\u16DF'];
/** Idle Vaelen nudges: first after this long without input, then every window after. */
const IDLE_FIRST_MS = 9000;
const IDLE_EVERY_MS = 16000;
const IDLE_MAX = 3;
/** Dwell before a hovered tier gets its quip, so scrubbing across the cards stays quiet. */
const PICK_QUIP_MS = 420;
/** Visual lock-in beat after confirm; menu audio is already cut. */
const CONFIRM_MIN_MS = 650;
const STICK_DEADZONE = 0.55;
const EMBERS = 90;
/** First lockup hold so the wordmark lands before the score bumper. */
const BEAT_OPEN_MS = 5200;
const BEAT_TITLE_MS = 7400;
const BEAT_BOARD_MS = 8600;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, parent?: HTMLElement, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  parent?.appendChild(node);
  return node;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

interface Ember {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  life: number;
  max: number;
  hue: number;
}

/**
 * Title screen before the cinematic. Key-art backdrop with a slow Ken Burns,
 * light sweeps and an ember canvas; stacked display wordmark; three difficulty cards
 * with a rune cursor; a replay-intro toggle. The first key or click unlocks
 * audio and starts the menu theme; until then a PRESS ANY KEY prompt pulses.
 * Keyboard (arrows/WASD/1-2-3, Enter/Space, I), mouse, touch and gamepad (d-pad/stick, A/Start, Y).
 */
class StartScreen {
  private readonly root: HTMLDivElement;
  private readonly cards: HTMLButtonElement[] = [];
  private readonly startButton: HTMLButtonElement;
  private readonly introToggle: HTMLButtonElement;
  private readonly caption: HTMLDivElement;
  private readonly captionText: HTMLSpanElement;
  private readonly nameField: HTMLInputElement;
  private readonly board: HTMLDivElement;
  private readonly boardList: HTMLOListElement;
  private readonly boardEmpty: HTMLParagraphElement;
  private readonly boardYou: HTMLParagraphElement;
  private boardRows: LeaderboardEntry[] = [];
  private readonly embers: HTMLCanvasElement;
  private readonly particles: Ember[] = [];
  private readonly audio: StartScreenAudio;
  private readonly status: HTMLElement;
  private armed = false;
  private ready = false;
  private done = false;
  private left = false;
  private replayIntro = false;
  private selected = difficultyIndex();
  private raf = 0;
  private lastFrame = 0;
  private lastInputAt = performance.now();
  private nextIdleAt = 0;
  private idleCount = 0;
  private quipTimer = 0;
  private captionTimer = 0;
  private beatTimer = 0;
  private beat: 'title' | 'board' = 'title';
  private padButtons: boolean[] = [];
  private padAxisX = 0;
  private padAxisY = 0;
  private prevCaption: AnnouncerAudioEngine['onCaption'] = null;
  private resolveDone: ((choice: StartChoice) => void) | null = null;

  constructor(opts: StartScreenOptions) {
    this.audio = opts.audio;
    this.status = opts.status;
    this.root = el('div', 'title-screen locked');

    const bg = el('div', 'title-bg', this.root);
    el('div', 'title-bg-css', bg);
    const art = el('img', 'title-art', bg);
    art.alt = '';
    art.decoding = 'async';
    art.src = ART_SRC;
    art.addEventListener('error', () => {
      art.remove();
      this.root.classList.add('no-art');
    });
    art.addEventListener('load', () => this.root.classList.add('art-ready'));
    el('div', 'title-sweep', this.root);
    el('div', 'title-sweep two', this.root);
    this.embers = el('canvas', 'title-embers', this.root);
    el('div', 'title-vignette', this.root);
    el('div', 'title-floor', this.root);

    const top = el('div', 'title-top', this.root);
    el('p', 'title-presents', top, 'ARCHMAGE VAELEN PRESENTS');
    const h1 = el('h1', 'title-h1', top);
    h1.dataset.text = 'WIZARD ARENA';
    el('span', 'title-lockup-fill', h1, 'WIZARD ARENA');
    const h2 = el('h2', 'title-h2', top);
    h2.dataset.text = 'TOTAL MAYHEM';
    el('span', 'title-lockup-fill', h2, 'TOTAL MAYHEM');
    const kicker = el('div', 'title-kicker', top);
    el('p', 'title-tag', kicker, 'Cast Fast. Blast Faster.');
    this.caption = el('div', 'title-caption', kicker);
    el('span', 'who', this.caption, 'ARCHMAGE VAELEN');
    this.captionText = el('span', 'line', this.caption);

    const namePlate = el('label', 'title-name', top);
    el('span', 'title-name-kicker', namePlate, 'CONTESTANT');
    this.nameField = el('input', 'title-name-field', namePlate);
    this.nameField.type = 'text';
    this.nameField.maxLength = CONTESTANT_NAME_MAX;
    this.nameField.autocomplete = 'off';
    this.nameField.spellcheck = false;
    this.nameField.autocapitalize = 'characters';
    this.nameField.setAttribute('autocorrect', 'off');
    this.nameField.setAttribute('enterkeyhint', 'done');
    this.nameField.setAttribute('aria-label', 'Contestant name');
    this.nameField.value = contestantName();
    namePlate.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (this.done) return;
      this.arm();
    });
    this.nameField.addEventListener('focus', this.onNameFocus);
    this.nameField.addEventListener('input', this.onNameInput);
    this.nameField.addEventListener('blur', this.onNameBlur);

    this.board = el('div', 'title-board', this.root);
    const boardHead = el('div', 'title-board-head', this.board);
    el('p', 'title-board-network', boardHead, 'ARCANE BROADCAST NETWORK');
    el('span', 'title-board-live', boardHead, 'ON AIR');
    const boardLock = el('div', 'title-board-lockup', this.board);
    const boardTitle = el('h2', 'title-board-word', boardLock);
    boardTitle.dataset.text = 'HIGH SCORES';
    el('span', 'title-lockup-fill', boardTitle, 'HIGH SCORES');
    el('p', 'title-board-kicker', boardLock, 'THE ALL-TIME TALLY');
    this.boardList = el('ol', 'title-board-list', this.board);
    this.boardEmpty = el('p', 'title-board-empty', this.board, 'NO CONTESTANTS ON THE BOARD');
    this.boardYou = el('p', 'title-board-you', this.board, `YOU ARE ${contestantName()}`);

    const coarseInput = window.matchMedia('(pointer: coarse)').matches;
    const bottom = el('div', 'title-bottom', this.root);
    const press = el('div', 'title-press', bottom);
    el('span', 'title-press-text', press, coarseInput ? 'TAP TO START' : 'PRESS ANY KEY');
    el('span', 'title-press-sub', press, 'to enter the broadcast');

    const menu = el('div', 'title-menu', bottom);
    el('p', 'title-menu-label', menu, 'CHOOSE YOUR TIER');
    const row = el('div', 'title-cards', menu);
    DIFFICULTIES.forEach((d, i) => {
      const card = el('button', 'title-card', row);
      card.type = 'button';
      card.dataset.tier = d.id;
      el('span', 'title-card-rune', card, RUNES[i] ?? '\u16A0');
      el('span', 'title-card-name', card, d.name.toUpperCase());
      el('span', 'title-card-blurb', card, d.blurb);
      el('span', 'title-card-stats', card, d.stats);
      el('i', 'title-card-glow', card);
      card.addEventListener('pointerenter', (e) => {
        // Touch/pen hover synthesis is unreliable; selection is driven by pointerup/click.
        if (e.pointerType !== 'mouse' || !this.armed || this.done) return;
        this.select(i, false);
      });
      card.tabIndex = -1;
      // pointerup covers touch taps; click covers mouse. Keyboard activation (detail 0) is handled in onKey.
      const pickCard = (e: Event): void => {
        const ue = e as MouseEvent;
        if ('detail' in ue && ue.detail === 0) return;
        e.preventDefault();
        if (!this.arm()) return;
        if (this.selected === i) void this.confirm();
        else this.select(i, true);
      };
      card.addEventListener('pointerup', (e) => {
        if (e.button !== 0) return;
        // Mouse still uses click so we don't double-fire with the click listener.
        if (e.pointerType === 'mouse') return;
        pickCard(e);
      });
      card.addEventListener('click', pickCard);
      this.cards.push(card);
    });

    const actions = el('div', 'title-actions', menu);
    this.startButton = el('button', 'title-start', actions, 'ENTER THE ARENA');
    this.startButton.type = 'button';
    this.startButton.tabIndex = -1;
    this.startButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (e.detail === 0 || !this.arm()) return;
      void this.confirm();
    });
    this.introToggle = el('button', 'title-toggle', actions);
    this.introToggle.type = 'button';
    this.introToggle.tabIndex = -1;
    el('i', 'title-toggle-box', this.introToggle);
    el('span', 'title-toggle-text', this.introToggle, 'Replay full intro');
    el('kbd', 'title-key', this.introToggle, 'I');
    this.introToggle.addEventListener('click', (e) => {
      e.preventDefault();
      if (e.detail === 0 || !this.arm()) return;
      this.toggleIntro();
    });

    el(
      'p',
      'title-controls',
      menu,
      coarseInput
        ? 'Tap a tier, then Enter the Arena  |  Left stick move  |  Dash button or double-flick  |  Right stick aim & cast'
        : 'Arrows / WASD / 1-2-3 or d-pad to choose  |  Enter, Space or A to lock in  |  Move: WASD or left stick, double-tap to dash, aim with mouse or right stick, hold fire',
    );
    bottom.appendChild(this.status);
    this.status.className = 'title-status';

    opts.host.appendChild(this.root);
    this.root.dataset.beat = this.beat;
    this.board.setAttribute('aria-hidden', 'true');
    this.renderSelection();
    this.renderBoard([]);
    void this.refreshBoard();
  }

  /** Resolves when the player locks in a tier. The screen is faded out but still mounted; call `dispose()` after the handoff. */
  run(): Promise<StartChoice> {
    return new Promise<StartChoice>((resolve) => {
      this.resolveDone = resolve;
      this.prevCaption = this.audio.announcer.onCaption;
      this.audio.announcer.onCaption = (text, ms) => this.showCaption(text, ms);
      window.addEventListener('keydown', this.onKey);
      window.addEventListener('pointerdown', this.onPointer);
      this.resizeEmbers();
      window.addEventListener('resize', this.resizeEmbers);
      window.visualViewport?.addEventListener('resize', this.resizeEmbers);
      this.lastFrame = performance.now();
      this.raf = requestAnimationFrame(this.frame);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.root.classList.add('show');
          this.beginRotation();
        }),
      );
    });
  }

  /** Renderer and atlas are loaded: ENTER THE ARENA becomes live. */
  setReady(): void {
    this.ready = true;
    this.root.classList.add('ready');
    if (!this.done) this.startButton.textContent = 'ENTER THE ARENA';
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    window.clearTimeout(this.quipTimer);
    window.clearTimeout(this.captionTimer);
    this.hideCaption();
    window.clearTimeout(this.beatTimer);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('pointerdown', this.onPointer);
    window.removeEventListener('resize', this.resizeEmbers);
    window.visualViewport?.removeEventListener('resize', this.resizeEmbers);
    this.nameField.removeEventListener('focus', this.onNameFocus);
    this.nameField.removeEventListener('input', this.onNameInput);
    this.nameField.removeEventListener('blur', this.onNameBlur);
    this.nameField.blur();
    this.audio.announcer.onCaption = this.prevCaption;
    this.root.remove();
  }

  /** First gesture: reveal the menu, unlock audio, start the theme. Returns false while the screen is finishing. */
  private arm(): boolean {
    if (this.done) return false;
    this.lastInputAt = performance.now();
    if (this.armed) {
      // A later key/click after a gamepad-only arm is the gesture the AudioContext needed.
      if (!this.audio.audioRunning()) void this.audio.playMenuMusic();
      return true;
    }
    this.armed = true;
    this.root.classList.remove('locked');
    this.root.classList.add('armed');
    this.nextIdleAt = performance.now() + IDLE_FIRST_MS;
    requestGameFullscreen();
    nudgeMobileChrome();
    void this.audio.playMenuMusic();
    return true;
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    if (this.done || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
    if (this.naming()) {
      if (e.code === 'Escape') {
        e.preventDefault();
        this.nameField.blur();
      } else if (e.code === 'Enter' || e.code === 'NumpadEnter') {
        e.preventDefault();
        this.nameField.blur();
      }
      this.lastInputAt = performance.now();
      return;
    }
    if (e.code === 'Tab' || e.code === 'Escape') return;
    if (e.code.startsWith('F') && e.code.length <= 3) return;
    const wasArmed = this.armed;
    if (!this.arm()) return;
    if (!wasArmed) {
      e.preventDefault();
      // First gesture was a confirm key: lock in immediately once the arena is ready.
      if (e.code === 'Enter' || e.code === 'Space' || e.code === 'NumpadEnter') void this.confirm();
      return;
    }
    switch (e.code) {
      case 'ArrowLeft':
      case 'KeyA':
      case 'ArrowUp':
      case 'KeyW':
        e.preventDefault();
        this.move(-1);
        break;
      case 'ArrowRight':
      case 'KeyD':
      case 'ArrowDown':
      case 'KeyS':
        e.preventDefault();
        this.move(1);
        break;
      case 'Digit1':
      case 'Numpad1':
        e.preventDefault();
        this.select(0, true);
        break;
      case 'Digit2':
      case 'Numpad2':
        e.preventDefault();
        this.select(1, true);
        break;
      case 'Digit3':
      case 'Numpad3':
        e.preventDefault();
        this.select(Math.min(2, DIFFICULTIES.length - 1), true);
        break;
      case 'Enter':
      case 'Space':
      case 'NumpadEnter':
        e.preventDefault();
        void this.confirm();
        break;
      case 'KeyI':
        e.preventDefault();
        this.toggleIntro();
        break;
      default:
        break;
    }
  };

  private readonly onPointer = (e: PointerEvent): void => {
    if (this.done) return;
    const target = e.target as HTMLElement | null;
    // Buttons handle their own clicks; the name plate focuses itself.
    if (target?.closest('button') || target?.closest('.title-name')) return;
    if (this.naming()) this.nameField.blur();
    this.arm();
  };

  private move(delta: number): void {
    const n = DIFFICULTIES.length;
    this.select((this.selected + delta + n) % n, true);
  }

  private select(index: number, viaInput: boolean): void {
    this.lastInputAt = performance.now();
    if (index === this.selected) return;
    this.selected = index;
    setDifficulty(DIFFICULTIES[index].id);
    this.renderSelection();
    this.audio.sfx.play(SfxId.MenuMove, viaInput ? 1 : 0.8);
    window.clearTimeout(this.quipTimer);
    this.quipTimer = window.setTimeout(() => this.quip(), PICK_QUIP_MS);
  }

  private quip(): void {
    if (this.done || !this.armed) return;
    const id = `menu_pick_${DIFFICULTIES[this.selected].id}`;
    if (this.audio.announcer.isSpeaking()) this.audio.announcer.interrupt();
    this.audio.announcer.say(id);
  }

  private renderSelection(): void {
    this.cards.forEach((card, i) => {
      card.classList.toggle('selected', i === this.selected);
      card.setAttribute('aria-pressed', i === this.selected ? 'true' : 'false');
    });
    this.root.dataset.tier = DIFFICULTIES[this.selected].id;
  }

  private beginRotation(): void {
    if (this.done || this.left) return;
    const startOnBoard = new URLSearchParams(window.location.search).has('board');
    if (startOnBoard) {
      this.beat = 'board';
      this.root.dataset.beat = 'board';
      this.board.setAttribute('aria-hidden', 'false');
      void this.refreshBoard();
      this.scheduleBeat(BEAT_BOARD_MS);
      return;
    }
    this.scheduleBeat(BEAT_OPEN_MS);
  }

  private scheduleBeat(ms: number): void {
    window.clearTimeout(this.beatTimer);
    this.beatTimer = window.setTimeout(() => this.flipBeat(), ms);
  }

  private flipBeat(): void {
    if (this.done || this.left) return;
    this.beat = this.beat === 'title' ? 'board' : 'title';
    this.root.dataset.beat = this.beat;
    this.board.setAttribute('aria-hidden', this.beat === 'board' ? 'false' : 'true');
    if (this.beat === 'board') {
      if (this.naming()) this.nameField.blur();
      this.hideCaption();
      void this.refreshBoard();
    }
    this.scheduleBeat(this.beat === 'board' ? BEAT_BOARD_MS : BEAT_TITLE_MS);
  }

  private async refreshBoard(): Promise<void> {
    const rows = await fetchTopScores(this.beat === 'board');
    if (this.done || this.left) return;
    this.renderBoard(rows);
  }

  private renderBoard(rows: LeaderboardEntry[]): void {
    this.boardRows = rows;
    this.boardList.replaceChildren();
    const empty = rows.length === 0;
    this.boardEmpty.hidden = !empty;
    this.boardList.hidden = empty;
    const you = contestantName();
    this.boardYou.textContent = `YOU ARE ${you}`;
    rows.forEach((row, i) => {
      const li = el('li', 'title-board-row', this.boardList);
      if (i < 3) li.classList.add(`top-${i + 1}`);
      if (row.outcome === 'victory') li.classList.add('champ');
      if (row.name === you) li.classList.add('you');
      el('span', 'title-board-rank', li, String(i + 1).padStart(2, '0'));
      el('span', 'title-board-name', li, row.name);
      el('span', 'title-board-score', li, row.score.toLocaleString('en-US'));
      el(
        'span',
        'title-board-meta',
        li,
        `${row.tier.toUpperCase()}  ·  FL ${Math.max(1, row.floor)}`,
      );
    });
  }

  private naming(): boolean {
    return document.activeElement === this.nameField;
  }

  private readonly onNameFocus = (): void => {
    this.nameField.select();
  };

  private readonly onNameInput = (): void => {
    const caret = this.nameField.selectionStart;
    const filtered = filterContestantInput(this.nameField.value);
    if (filtered !== this.nameField.value) {
      this.nameField.value = filtered;
      if (caret !== null) {
        const next = Math.min(caret, filtered.length);
        this.nameField.setSelectionRange(next, next);
      }
    }
    if (filtered.trim().length >= 2) {
      const stored = setContestantName(filtered);
      if (stored === filtered.trim()) this.syncNameSurfaces();
    }
  };

  private readonly onNameBlur = (): void => {
    this.nameField.value = setContestantName(this.nameField.value);
    this.syncNameSurfaces();
  };

  private syncNameSurfaces(): void {
    this.boardYou.textContent = `YOU ARE ${contestantName()}`;
    this.renderBoard(this.boardRows);
  }

  private toggleIntro(): void {
    this.lastInputAt = performance.now();
    this.replayIntro = !this.replayIntro;
    this.introToggle.classList.toggle('on', this.replayIntro);
    this.introToggle.setAttribute('aria-pressed', this.replayIntro ? 'true' : 'false');
    this.audio.sfx.play(SfxId.MenuMove, 0.7);
  }

  private async confirm(): Promise<void> {
    if (this.done) return;
    if (!this.arm()) return;
    requestGameFullscreen();
    if (!this.ready) {
      this.root.classList.add('not-ready');
      this.startButton.textContent = 'LOADING…';
      this.status.textContent = 'Still summoning the arena — hang on.';
      window.setTimeout(() => {
        if (!this.ready && !this.done) this.startButton.textContent = 'ENTER THE ARENA';
        this.root.classList.remove('not-ready');
      }, 500);
      return;
    }
    this.done = true;
    this.nameField.value = setContestantName(this.nameField.value);
    this.nameField.blur();
    window.clearTimeout(this.quipTimer);
    window.clearTimeout(this.captionTimer);
    this.hideCaption();
    window.clearTimeout(this.beatTimer);
    saveDifficulty();
    this.root.classList.add('confirmed');
    this.startButton.textContent = 'LOCKED IN';
    // Audio must never trap the player on the title screen.
    try {
      this.audio.announcer.interrupt(true);
      this.audio.leaveMenu();
    } catch (err) {
      console.warn('leaveMenu failed', err);
    }
    try {
      this.audio.sfx.play(SfxId.MenuConfirm, 1);
    } catch {
      // procedural sfx is best-effort
    }
    await sleep(CONFIRM_MIN_MS);
    this.left = true;
    this.root.classList.add('leaving');
    const resolve = this.resolveDone;
    this.resolveDone = null;
    resolve?.({ replayIntro: this.replayIntro });
  }

  private hideCaption(): void {
    this.caption.classList.remove('show');
    this.root.classList.remove('caption-on');
  }

  private showCaption(text: string, ms: number): void {
    if (this.done || this.beat !== 'title') return;
    if (this.naming()) this.nameField.blur();
    window.clearTimeout(this.captionTimer);
    this.captionText.textContent = text;
    this.caption.classList.add('show');
    this.root.classList.add('caption-on');
    this.captionTimer = window.setTimeout(() => this.hideCaption(), ms);
  }

  private readonly frame = (now: number): void => {
    const dt = Math.min(0.05, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    this.audio.announcer.update();
    if (!this.left) {
      this.pollGamepad();
      this.drawEmbers(dt);
    }
    if (this.armed && !this.done && this.idleCount < IDLE_MAX && now >= this.nextIdleAt && now - this.lastInputAt >= IDLE_FIRST_MS) {
      if (!this.audio.announcer.isSpeaking() && this.audio.announcer.say('menu_idle')) this.idleCount++;
      this.nextIdleAt = now + IDLE_EVERY_MS;
    }
    this.raf = requestAnimationFrame(this.frame);
  };

  private pollGamepad(): void {
    if (this.done) return;
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let pad: Gamepad | null = null;
    for (const p of pads) {
      if (p && p.connected) {
        pad = p;
        break;
      }
    }
    if (!pad) return;
    const pressed = (i: number): boolean => !!pad.buttons[i]?.pressed;
    const edge = (i: number): boolean => pressed(i) && !this.padButtons[i];
    const anyEdge = pad.buttons.some((b, i) => b.pressed && !this.padButtons[i]);
    const ax = pad.axes[0] ?? 0;
    const ay = pad.axes[1] ?? 0;
    const stepX = Math.abs(ax) > STICK_DEADZONE ? Math.sign(ax) : 0;
    const stepY = Math.abs(ay) > STICK_DEADZONE ? Math.sign(ay) : 0;
    const stickMove = (stepX !== 0 && stepX !== this.padAxisX ? stepX : 0) || (stepY !== 0 && stepY !== this.padAxisY ? stepY : 0);

    if (this.naming() && (anyEdge || stickMove)) this.nameField.blur();
    if (!this.armed) {
      if (anyEdge || stickMove) this.arm();
    } else {
      if (edge(14) || stickMove < 0) this.move(-1);
      if (edge(15) || stickMove > 0) this.move(1);
      if (edge(12)) this.move(-1);
      if (edge(13)) this.move(1);
      if (edge(0) || edge(9)) void this.confirm();
      if (edge(3)) this.toggleIntro();
    }
    this.padButtons = pad.buttons.map((b) => b.pressed);
    this.padAxisX = stepX;
    this.padAxisY = stepY;
  }

  private readonly resizeEmbers = (): void => {
    const dpr = Math.min(1.5, window.devicePixelRatio || 1);
    const { w, h } = viewportSize();
    this.embers.width = Math.floor(w * dpr);
    this.embers.height = Math.floor(h * dpr);
  };

  private spawnEmber(e: Ember, w: number, h: number, fresh: boolean): void {
    e.x = Math.random() * w;
    e.y = fresh ? Math.random() * h : h + 10;
    e.vx = (Math.random() - 0.5) * 18;
    e.vy = -(14 + Math.random() * 30);
    e.r = 0.8 + Math.random() * 1.9;
    e.max = 5 + Math.random() * 7;
    e.life = fresh ? Math.random() * e.max : 0;
    e.hue = Math.random() < 0.7 ? 28 + Math.random() * 14 : 275 + Math.random() * 20;
  }

  private drawEmbers(dt: number): void {
    const ctx = this.embers.getContext('2d');
    if (!ctx) return;
    const w = this.embers.width;
    const h = this.embers.height;
    const scale = h / 900;
    if (this.particles.length === 0) {
      for (let i = 0; i < EMBERS; i++) {
        const e: Ember = { x: 0, y: 0, vx: 0, vy: 0, r: 1, life: 0, max: 1, hue: 30 };
        this.spawnEmber(e, w, h, true);
        this.particles.push(e);
      }
    }
    ctx.clearRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'lighter';
    for (const e of this.particles) {
      e.life += dt;
      if (e.life >= e.max || e.y < -10) {
        this.spawnEmber(e, w, h, false);
        continue;
      }
      e.vx += Math.sin(e.life * 2.1 + e.r) * 9 * dt;
      e.x += e.vx * scale * dt;
      e.y += e.vy * scale * dt;
      const t = e.life / e.max;
      const a = Math.sin(t * Math.PI) * 0.85;
      const r = e.r * scale * (1.6 - t * 0.6);
      ctx.fillStyle = `hsla(${e.hue}, 100%, ${e.hue > 100 ? 72 : 62}%, ${a})`;
      ctx.beginPath();
      ctx.arc(e.x, e.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  }
}

export interface StartScreenHandle {
  /** Resolves with the player's choice once ENTER THE ARENA is confirmed. */
  choice: Promise<StartChoice>;
  setReady: () => void;
  dispose: () => void;
}

export function showStartScreen(opts: StartScreenOptions): StartScreenHandle {
  const screen = new StartScreen(opts);
  return { choice: screen.run(), setReady: () => screen.setReady(), dispose: () => screen.dispose() };
}
